import { ajaxByUser, ajaxCatch } from '@helper/api';
import { getProfiBazaApiClient } from '@services/ProfiBazaApi';
import {
	InterventionCommentType,
	InterventionPath,
} from '@services/src/models';
import { ProfiBazaAPIModels } from '@services/src/profiBazaAPI';
import { IAccountDetailsStore } from 'account/accountDetailsStore';
import { Col, Row, Select } from 'antd';
import Modal from 'antd/lib/modal/Modal';
import { Field, Formik, FormikActions, FormikProps } from 'formik';
import {
	FFieldLabel,
	FRadioGroup,
	FSelect,
	FTextArea,
} from 'forms/FormikFormItems';
import { useRootData } from 'hooks/hook';
import { CenteredRow } from 'layout/CenteredRow';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';

import { AddInterventionCommentDto } from '../commands/AddInterventionCommentCommand';
import { UpdateInterventionCommentDto } from '../commands/UpdateInterventionCommentCommand';
import { getCommentFieldsAccordingToPath } from '../store/InterventionCommentStore';

export interface InterventionCommentModalProps {
	interventionId: string;
	commentId: string | undefined;
	editable: boolean;
	visible: boolean;
	setVisible: (visible: boolean) => void;
	onSave: () => void;
	interventionSubjectId: string;
	interventionPath: InterventionPath;
}

const CommentModal: React.FC<InterventionCommentModalProps> = (props) => {
	const {
		interventionId,
		commentId,
		visible,
		setVisible,
		onSave,
		interventionSubjectId,
	} = props;

	const accountDetailsStore: IAccountDetailsStore = useRootData(
		(store) => store.accountDetailsStore
	);

	const [isProcessed, setIsProcessed] = useState<boolean>(false);
	const [updateModel, setUpdateModel] = useState<
		UpdateInterventionCommentDto
	>();

	useEffect(() => {
		if (commentId) {
			ajaxCatch(() =>
				getProfiBazaApiClient().then((api) => {
					return api.interventionComments
						.getById(commentId)
						.then(
							(
								result: ProfiBazaAPIModels.InterventionCommentDto
							) => {
								setIsProcessed(
									ProfiBazaAPIModels.InterventionCommentStatus
										.PROCESSED == result?.status!
								);
								setUpdateModel(
									new UpdateInterventionCommentDto(
										result,
										commentId
									)
								);
							}
						);
				})
			);
		}
	}, [commentId]);

	const handleCancel = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
		setVisible(false);
	};

	const handleCreate = (
		values: AddInterventionCommentDto,
		actions: FormikActions<
			ProfiBazaAPIModels.CreateInterventionCommentCommand
		>
	) => {
		const command = AddInterventionCommentDto.toCommand(values);
		ajaxByUser(
			'Dodano komentarz',
			() =>
				getProfiBazaApiClient().then((api) =>
					api.interventionComments
						.create({
							body: command,
						})
						.then((response) => {
							return response;
						})
				),
			() => {
				setVisible(false);
				actions.resetForm();
				onSave();
			},
			(errors) => {
				actions.setErrors(errors);
			}
		).then(() => actions.setSubmitting(false));
	};

	const renderBody = (formikProps: FormikProps<any>) => {
		return (
			<>
				{accountDetailsStore.account.get()?.subject?.id ==
					interventionSubjectId &&
					!props.editable && (
						<CenteredRow>
							<Col span={20}>
								{isProcessed && (
									<p style={{ fontWeight: 'bold' }}>
										Komentarz rozpatrzony
									</p>
								)}
							</Col>
						</CenteredRow>
					)}
				{!props.commentId && (
					<CenteredRow>
						<Col span={20}>
							<FFieldLabel label="Nazwa pola" for="fieldName" />
							<Field
								label="Nazwa pola"
								component={FSelect}
								name="fieldName"
								showSearch={true}
								children={Object.entries(
									getCommentFieldsAccordingToPath(
										props.interventionPath,
										null
									)
								).map((group, i) => (
									<Select.OptGroup label={group[0]}>
										{Object.entries(group[1]).map(
											(item, i) => (
												<Select.Option
													key={item[0]}
													value={item[0]}
												>
													{item[1]}
												</Select.Option>
											)
										)}
									</Select.OptGroup>
								))}
							/>
						</Col>
					</CenteredRow>
				)}
				<CenteredRow>
					<Col span={20}>
						<FFieldLabel label="Komentarz" for="description" />
						<Field
							component={FTextArea}
							label="Tutaj wpisz swój komentarz"
							name="description"
							placeholder="Tutaj wpisz swój komentarz"
							readOnly={!props.editable}
						/>
					</Col>
				</CenteredRow>
				{!props.commentId && (
					<Row>
						<Col span={2}></Col>
						<Col span={22} className="commentType">
							<FFieldLabel
								label="Typ komentarza"
								for="commentType"
							/>
							<Field
								readOnly={false}
								style={{ width: '100%' }}
								defaultValue={InterventionCommentType.PUBLIC}
								values={[
									InterventionCommentType.PUBLIC,
									InterventionCommentType.PRIVATE,
									InterventionCommentType.IMPORTANT,
								]}
								labels={[
									'Informacyjny publiczny',
									'Informacyjny w ramach podmiotu',
									'Ważny',
								]}
								component={FRadioGroup}
								name="commentType"
							/>
						</Col>
					</Row>
				)}
			</>
		);
	};

	const handleUpdate = (
		values: UpdateInterventionCommentDto,
		actions: FormikActions<UpdateInterventionCommentDto>
	) => {
		const command = UpdateInterventionCommentDto.toCommand(values);
		ajaxByUser(
			'Zaktualizowano komentarz',
			() =>
				getProfiBazaApiClient().then((api) =>
					api.interventionComments.update({
						body: command,
					})
				),
			() => {
				setVisible(false);
				actions.resetForm();
				setUpdateModel(undefined);
				onSave();
			},
			(errors) => {
				actions.setErrors(errors);
			}
		).then(() => actions.setSubmitting(false));
	};

	return (
		<>
			{props.commentId === undefined ? (
				<Formik
					validateOnChange
					validateOnBlur
					initialValues={{
						interventionId: interventionId,
						fieldName: undefined,
						description: undefined,
						commentType:
							ProfiBazaAPIModels.InterventionCommentType.PUBLIC,
						activityId: undefined,
					}}
					enableReinitialize
					validationSchema={Yup.object().shape({
						description: Yup.string()
							.nullable()
							.required("Pole 'Komentarz' jest wymagane.")
							.max(
								1000,
								"Pole 'Komentarz' nie może być dłuższe niż 1000 znaków."
							),
						fieldName: Yup.string()
							.nullable()
							.required("Pole 'Nazwa pola' nie może być puste."),
					})}
					isInitialValid
					onSubmit={handleCreate}
					render={(
						formikProps: FormikProps<
							ProfiBazaAPIModels.CreateInterventionCommentCommand
						>
					) => (
						<Modal
							title="Dodawanie komentarza"
							visible={visible}
							onOk={(e) => {
								formikProps.submitForm();
							}}
							okButtonProps={{
								disabled: formikProps.isSubmitting,
							}}
							cancelButtonProps={{
								disabled: formikProps.isSubmitting,
							}}
							cancelText="Anuluj"
							centered
							onCancel={handleCancel}
							okText="Dodaj"
							destroyOnClose
						>
							{renderBody(formikProps)}
						</Modal>
					)}
				/>
			) : (
				<Formik
					validateOnChange
					validateOnBlur
					initialValues={updateModel || {}}
					enableReinitialize
					isInitialValid
					onSubmit={handleUpdate}
					validationSchema={Yup.object().shape({
						description: Yup.string()
							.nullable()
							.required("Pole 'Komentarz' jest wymagane.")
							.max(
								1000,
								"Pole 'Komentarz' nie może być dłuższe niż 1000 znaków."
							),
					})}
					render={(
						formikProps: FormikProps<UpdateInterventionCommentDto>
					) => (
						<Modal
							title={
								props.editable
									? 'Zaktualizuj komentarz'
									: 'Szczegóły komentarza'
							}
							visible={visible}
							onOk={() => {
								formikProps.submitForm();
							}}
							okButtonProps={{
								disabled: formikProps.isSubmitting,
								hidden: props.editable ? false : true,
							}}
							cancelButtonProps={{
								disabled: formikProps.isSubmitting,
							}}
							cancelText={props.editable ? 'Anuluj' : 'Zamknij'}
							centered
							onCancel={handleCancel}
							okText={'Aktualizuj'}
							destroyOnClose
						>
							{renderBody(formikProps)}
						</Modal>
					)}
				/>
			)}
		</>
	);
};

export default observer(CommentModal);
